import locales_es from '../locales/es';

class AppHelpers {
  /*
   *
   * Utility to check if a variable named num (possibly a string) is effectively a number
   */
  isNumeric(num) {
    return (
      !isNaN(num) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
      !isNaN(parseFloat(num))
    ); // ...and ensure strings of whitespace fail
  }

  hasAtLeastOneDigit(str) {
    return /\d/.test(str);
  }

  validateIdentification(countryId, identification, identificationTypeId) {
    // Argentina
    if (countryId === 1) {
      if (!identification) {
        return {
          title: locales_es.patientValidationNoIdentification,
          message: locales_es.patientValidationNoIdentificationExplanation,
          fieldValue: identification,
          fieldName: 'identification',
          type: 'numeric',
          fieldTitle: locales_es.identification,
          fieldPlaceholder: locales_es.identification,
        };
      }

      if (identificationTypeId === 1) {
        // Debe contener al menos un dígito
        if (!this.hasAtLeastOneDigit(identification)) {
          return {
            title: locales_es.patientValidationInvalidIdentification,
            message: locales_es.patientValidationInvalidCouldHaveNumber,
            fieldValue: identification,
            fieldName: 'identification',
            type: 'numeric',
            fieldTitle: locales_es.identification,
            fieldPlaceholder: locales_es.identification,
          };
        }

        if (
          Number(identification) > 150000000 ||
          Number(identification) < 10000
        ) {
          return {
            title: locales_es.patientValidationInvalidIdentificationNumber,
            message:
              locales_es.patientValidationInvalidIdentificationExplanationNumber,
            fieldValue: identification,
            fieldName: 'identification',
            type: 'numeric',
            fieldTitle: locales_es.identification,
            fieldPlaceholder: locales_es.identification,
          };
        }
      }
    }
    return true; // valid
  }

  /*
   *
   * Utility to get ErrorMessages from ApiService responses
   */
  checkPatientIntegrity(patientData) {
    if (patientData && patientData.country_id) {
      const validation = this.validateIdentification(
        patientData.country_id,
        patientData.identification,
        patientData.identification_type_id,
      );
      return validation;
    }
    return false;
  }

  patientDataValidatorErrors(patientData: any) {
    const errors: any = [];
    const messages: any = {};
    let hasErrorFlag = false;
    
    if (!patientData.identification) {
      errors.push('identification');
      messages.identification = 'El Campo es requerido';
    } else {
      const [message, result] = this.validateIdentificationV2(patientData.identification, patientData.identification_type.name)
      
      if (!result) {
        errors.push('identification');
        messages.identification = message;
      }
    }
    if (!patientData.birth_date) {
      errors.push('birthDate');
      messages.birthDate = 'El Campo es requerido';
    }
    
    if (!patientData.gender_id || !patientData.gender) {
      errors.push('gender');
      messages.gender = 'El Campo es requerido';
    }

    const isPhoneReq = Boolean(
      patientData.medical_insurance?.phone_required === true ||
      patientData.medical_insurance?.phone_required === 1 ||
      patientData.medical_insurance?.phone_required === "1" ||
      patientData.medical_insurance?.medical_insurance_phone_required === true ||
      patientData.medical_insurance?.medical_insurance_phone_required === 1 ||
      patientData.medical_insurance?.medical_insurance_phone_required === "1" ||
      patientData.phone_required === true ||
      patientData.phone_required === 1 ||
      patientData.phone_required === "1"
    );

    if (isPhoneReq && !patientData.cell_phone) {
      errors.push('phoneNumber');
      messages.phoneNumber = 'El Campo es requerido';
    }

    hasErrorFlag = errors.length > 0;

    return [errors, messages, hasErrorFlag];
  }

  normalizeDocType = (t: any) => String(t ?? "").trim().toUpperCase()

  getDocRules (docTypeRaw: any) {
    const docType = this.normalizeDocType(docTypeRaw);

    const MAP: any = {
      "PASAPORTE": { min: 5, max: 12, label: "Pasaporte" },
      "DNI": { min: 4, max: 9, label: "DNI" },
      "CI": { min: 6, max: 9, label: "Cédula de identidad" },
      "CEDULA": { min: 6, max: 9, label: "Cédula de identidad" },
      "CÉDULA": { min: 6, max: 9, label: "Cédula de identidad" },
      "LIBRETA CIVICA": { min: 1, max: 16, label: "Libreta cívica" },
      "LIBRETA CÍVICA": { min: 1, max: 16, label: "Libreta cívica" },
      "LIBRETA DE ENROLAMIENTO": { min: 1, max: 16, label: "Libreta de enrolamiento" },
      "LE": { min: 1, max: 16, label: "Libreta de enrolamiento" },
      "LC": { min: 1, max: 16, label: "Libreta cívica" },
    };

    return MAP[docType] ?? { min: 1, max: 16, label: "Documento" };
  };

  validateIdentificationV2(identification: any, identificationType: any, state?: any) {
    
    const { min, max } = this.getDocRules(identificationType);

    const val = String(identification || "");
    const len = val.length;
    const alnum = /^[A-Za-z0-9]+$/; // sin espacios ni especiales
    const onlyLetters = /^[A-Za-z]+$/; // solo letras

    if (!val || val.trim() === "") {
      return ["El Campo es obligatorio", false];
    }

    if (!alnum.test(val)) {
      const base = `El Campo solo puede contener letras y números, sin espacios ni caracteres especiales.`;
      if (len < min) return [`${base} Debe contener al menos ${min} caracteres.`, false];
      if (len > max) return [`${base} Debe contener como máximo ${max} caracteres.`, false];
      return [base, false];
    }

    if (onlyLetters.test(val)) {
      return ["El campo debe contener dígitos.", false, state && state];
    }

    if (len < min) {
      return [`Debe contener al menos ${min} caracteres.`, false, state && state];
    }
    if (len > max) {
      return [`Debe contener como máximo ${max} caracteres.`, false, state && state];
    }

    // Solo aplicar reglas especiales para DNI
    if (this.normalizeDocType(identificationType) === "DNI" ) {
      const [message, result] = this.validatingDNIFormats(val, len);
  
      if (!result) {
        return [message, result];
      }

    }

    return ["", true];
  }

  validatingDNIFormats(val: any, len: number) {
  
      const formato1 = /^[MF]\d{4,7}$/;      // M/F + 4-7 dígitos
      const formato2 = /^\d{4,7}[MF]$/;      // 4-7 dígitos + M/F
      const formato3 = /^\d{4,8}$/;          // 4-8 dígitos
  
      if (!val || val.trim() === "") {
        return ["El Campo es obligatorio", false];
      }
  
      if (formato1.test(val)) {
        return ["", true];
      }
      if (formato2.test(val)) {
        return ["", true];
      }
      if (formato3.test(val)) {
        return ["", true];
      }
  
      // Mensajes específicos según el error
      if (/^[MF]/.test(val) && !/^[MF]\d{4,7}$/.test(val)) {
        return [
          "Formato inválido. Si comienza con M o F, debe seguirse de 4 a 7 dígitos. Ejemplo: M1234, F9876543.",
          false,
        ];
      }
      if (/[MF]$/.test(val) && !/^\d{4,7}[MF]$/.test(val)) {
        return [
          "Formato inválido. Si termina con M o F, debe estar precedido de 4 a 7 dígitos. Ejemplo: 1234M, 7654321F.",
          false,
        ];
      }
      if (/^\d+$/.test(val) && (len < 4 || len > 8)) {
        return [
          "Formato inválido. Si solo son números, deben ser entre 4 y 8 dígitos. Ejemplo: 1234, 87654321.",
          false,
        ];
      }
  
      // Mensaje general si no coincide ningún caso anterior
      return [
        "Formato inválido. Ejemplos válidos: M1234, 1234M, 1234, F9876543, 7654321F, 87654321.",
        false,
      ];
  }
}


//unregister();

export default AppHelpers;
